/**
 * RunsHistoryPanel — last-N runs for the active model.
 *
 * Loads via api.getModelRuns when opened (no eager polling). Renders a
 * compact list with timestamp + status + execution time. The active
 * lastRun in the store is highlighted.
 */

import React, { useEffect, useState } from 'react';
import { Clock, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import api from '../../apiService';
import type { ModelRunSummary, RunStatus } from '../../types/modeling';
import { formatDateTime } from '../../utils/formatDateTime';

interface RunsHistoryPanelProps {
  projectId: string;
  modelId: string;
  authToken: string;
  userId?: string;
  /** When non-null, the matching run is highlighted as "active". */
  activeRunId?: string | null;
  /** Bumped each time a run completes so the list refreshes. Accepts
   *  any value identity-comparable across renders — typically the
   *  latest run_id string. */
  refreshKey?: string | number;
}

const LIMIT = 5;

export default function RunsHistoryPanel({
  projectId,
  modelId,
  authToken,
  userId,
  activeRunId,
  refreshKey,
}: RunsHistoryPanelProps) {
  const [runs, setRuns] = useState<ModelRunSummary[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // The run we expect to see at the top after a just-finished/in-flight run.
    // A fresh run can briefly lag the list query (creation/propagation), or the
    // panel may be opened mid-run — so if it's missing we re-poll a few times
    // rather than leaving the user staring at only old runs (owner #1).
    const expectedRunId =
      (activeRunId && String(activeRunId)) ||
      (typeof refreshKey === 'string' ? refreshKey : '');

    const fetchOnce = async (attempt: number) => {
      if (cancelled) return;
      if (attempt === 0) setState('loading');
      setError(null);
      try {
        const result = await api.getModelRuns(projectId, modelId, authToken, userId);
        if (cancelled) return;
        const list = (result?.runs || result || []) as ModelRunSummary[];
        const top = Array.isArray(list) ? list.slice(0, LIMIT) : [];
        setRuns(top);
        setState('idle');
        const missing = !!expectedRunId && !top.some((r) => r.run_id === expectedRunId);
        if (missing && attempt < 4) {
          timer = setTimeout(() => fetchOnce(attempt + 1), 800);
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load run history');
        setState('error');
      }
    };

    fetchOnce(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [projectId, modelId, authToken, userId, refreshKey, activeRunId]);

  return (
    <div className="w-[340px] bg-surface border border-border-default rounded-md shadow-bark-md p-3">
      <div className="flex items-center justify-between mb-2.5">
        <span className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em]">
          Last {LIMIT} runs
        </span>
        {state === 'loading' && (
          <Loader2 className="w-3 h-3 text-fg-tertiary animate-spin" strokeWidth={1.5} />
        )}
      </div>

      {state === 'error' && (
        <div className="text-[12px] text-danger">{error}</div>
      )}

      {state !== 'error' && runs.length === 0 && state !== 'loading' && (
        <div className="text-[12px] text-fg-tertiary italic">
          No runs yet — click Run model to record the first one.
        </div>
      )}

      {runs.length > 0 && (
        <ul className="space-y-1.5">
          {runs.map((run) => {
            const isActive = run.run_id === activeRunId;
            return (
              <li
                key={run.run_id}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-md border ${
                  isActive
                    ? 'border-accent bg-accent-tint'
                    : 'border-border-default bg-bg-warm/30'
                }`}
              >
                <RunStatusIcon status={run.status} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-mono text-[10px] uppercase tracking-wider font-semibold ${
                        statusColorClass(run.status)
                      }`}
                    >
                      {run.status}
                    </span>
                    {run.execution_time_ms !== undefined && (
                      <span className="font-mono text-[10px] text-fg-tertiary">
                        {formatTime(run.execution_time_ms)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-fg-tertiary">
                    <Clock className="w-3 h-3" strokeWidth={1.5} />
                    <span>{formatTimestamp(run.created_at)}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RunStatusIcon({ status }: { status: RunStatus }) {
  if (status === 'completed') {
    return <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" strokeWidth={1.5} />;
  }
  if (status === 'failed') {
    return <XCircle className="w-4 h-4 text-danger flex-shrink-0" strokeWidth={1.5} />;
  }
  if (status === 'running') {
    return (
      <Loader2 className="w-4 h-4 text-warning animate-spin flex-shrink-0" strokeWidth={1.5} />
    );
  }
  return <Clock className="w-4 h-4 text-fg-tertiary flex-shrink-0" strokeWidth={1.5} />;
}

function statusColorClass(status: RunStatus): string {
  switch (status) {
    case 'completed':
      return 'text-success';
    case 'failed':
      return 'text-danger';
    case 'running':
      return 'text-warning';
    default:
      return 'text-fg-tertiary';
  }
}

function formatTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 1000)}s`;
}

function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return formatDateTime(d);
}
